/** Reach state rules: `src/data/reach-state.json`, Zod-validated here (M3 plan R5). */
import { z } from 'zod'
import { galaxyAnchorIds, galaxyRegionIds } from '../../data/galaxyConfig'
import stateJson from '../../data/reach-state.json'

const key = z.string().min(1)
const bytes = z.number().int().positive()

export const reachStateConfigSchema = z
  .strictObject({
    comment: z.string().optional(),
    /** The canonical Reach when there is no `?galaxy=` and no save. */
    defaultGalaxySeed: z.number().int().nonnegative(),
    knowledge: z.strictObject({
      chartedRegions: z.array(z.enum(galaxyRegionIds)),
      rumourHops: z.number().int().nonnegative().max(8),
      rumouredAnchors: z.array(z.enum(galaxyAnchorIds)),
    }),
    save: z.strictObject({ key, prevKey: key, corruptKey: key, warnBytes: bytes, maxBytes: bytes }),
    autosave: z.strictObject({ everySeconds: z.number().positive() }),
  })
  .refine((c) => c.save.warnBytes <= c.save.maxBytes, { message: 'save.warnBytes must not exceed save.maxBytes', path: ['save', 'warnBytes'] })
  .refine((c) => new Set([c.save.key, c.save.prevKey, c.save.corruptKey]).size === 3, { message: 'save keys must differ', path: ['save'] })

export type ReachStateConfig = z.infer<typeof reachStateConfigSchema>
export type KnowledgeConfig = ReachStateConfig['knowledge']
export type SaveConfig = ReachStateConfig['save']
export type AutosaveConfig = ReachStateConfig['autosave']

/** Parses and validates reach-state.json (throws a ZodError naming the bad field). */
export function loadReachStateConfig(raw: unknown = stateJson): ReachStateConfig {
  return reachStateConfigSchema.parse(raw)
}
